import React, { useEffect, useRef } from "react";
import logo from "./assets/logofinal-flat.png";
import event1Image from "./assets/live-events/event1.jpg";
import event2Image from "./assets/live-events/event2.jpg";
import event3Image from "./assets/live-events/event3.jpg";
import event4Image from "./assets/live-events/event4.jpg";
import event5Image from "./assets/live-events/event5.jpg";
import event1Video from "./assets/live-events/event1.mp4";
import event3Video from "./assets/live-events/event3.mp4";
import event5Video from "./assets/live-events/event5.mp4";
import "./HomeV2.css";
import "./LiveEventsPage.css";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";

const experiences = [
  {
    id: "polyphonic-concert",
    number: "01",
    category: "Concerts & festivals",
    title: "The Polyphonic Concert: Pit, Stage & Control Booth",
    lead:
      "Bring together pristine soundboard audio, a stabilized stage view and the raw pulse of the crowd in one synchronized living concert experience.",
    problem:
      "Concert recordings on social media often sit at two extremes: highly polished aftermovies published weeks later, or smartphone clips with distorted bass. Neither fully recreates the interplay between stage performance, soundboard acoustics and crowd energy.",
    layout: "Symphony",
    layoutDetail: "Multi-track audio synchronization and acoustic footprinting",
    workflowTitle: "Choral architecture",
    workflow: [
      "Slot 1 — Master Audio: direct mixing-console feed managed by the front-of-house sound engineer.",
      "Slot 2 — Lead Performer: stabilized on-stage camera following the lead artist or soloist.",
      "Slot 3 — Crowd Pulse: ultra-wide-angle micro-capture from the center of the pit by an accredited fan Weaver.",
      "AI aligns audio and video timing using snare or kick-drum transients, reducing the acoustic latency created by the physical distance between stage, crowd and sound desk.",
    ],
    impact:
      "Create an immersive Digital Live Pass for remote fans who could not secure a physical ticket.",
    monetization:
      "Illustrative Smart Escrow split: a €1.50 digital stream micropayment can be divided 60% to the band/production, 20% to the FOH sound engineer and 20% to the pit Weavers whose camera angles are viewed.",
    image: event1Image,
    video: event1Video,
    videoPortrait: true,
  },
  {
    id: "behind-the-curtain",
    number: "02",
    category: "Theatre, dance & opera",
    title: "Behind the Curtain & Synchronized Stage Management",
    lead:
      "Show the performance and the invisible technical choreography at the same moment, without forcing audiences to choose one perspective.",
    problem:
      "Performing-arts audiences rarely see the rapid set changes, stage-management cues and wardrobe work unfolding in the wings. Traditional frontal recordings also struggle to capture the technical craft that younger audiences increasingly expect to discover.",
    layout: "Versus",
    layoutDetail: "Synchronized 50/50 split-screen",
    workflowTitle: "Simultaneous dual-view",
    workflow: [
      "Left side: the classic stage view — actors, dancers or singers performing under the spotlights.",
      "Right side: infrared or low-light backstage view — rigging, stage managers whispering cues and fast costume changes before an entrance.",
      "The viewer can follow both synchronized streams side by side and appreciate the artistic and technical choreography as one story.",
    ],
    impact:
      "Elevate behind-the-scenes professions, modernize the institution's public image and create high-value educational material for drama schools and conservatories.",
    monetization:
      "Verified technicians and performers can be represented as Weavers, with a configurable portion of seasonal digital subscriptions routed to them as trackable neighboring-rights revenue.",
    image: event2Image,
    sideTitle: true,
  },
  {
    id: "living-aftermovie",
    number: "03",
    category: "Open-air festivals & large-scale events",
    title: 'The Participatory "Living Aftermovie"',
    lead:
      "Turn the first morning after a festival into a publishing moment, not the start of a multi-week editing cycle.",
    problem:
      "An official festival aftermovie can take 15 to 30 days and require an expensive post-production team. By then, attendee excitement and organic social sharing have already dropped.",
    layout: "Seed",
    layoutDetail: "Decentralized community curation",
    workflowTitle: "A narrative blueprint fed by the crowd",
    workflow: [
      "The organizer defines the festival blueprint: Arrival & Campsite, Sunset Electro Stage, Mainstage Climax and Late-Night Food Trucks.",
      "Designated festivalgoers and accredited creators submit 10-second micro-clips into open slots.",
      "AI Content Guard audits stabilization and audio clarity, filters inappropriate content and can apply automatic background-face blurring for GDPR-oriented privacy handling.",
      "The final living HLS stream can auto-compile when the gates close, keeping one persistent URL alive as new material is curated.",
    ],
    impact:
      "Publish an official, certified aftermovie as participants wake up and use the same persistent link to support immediate early-bird promotion for the next edition.",
    monetization:
      "Illustrative Bounties & Tipping model: €50–€150 bounties can be released from the festival Smart Escrow to Weavers whose shots are selected, while audiences can tip favorite creators directly.",
    image: event3Image,
    video: event3Video,
  },
  {
    id: "corporate-debrief",
    number: "04",
    category: "MICE seminars & corporate conventions",
    title: "The Choral Corporate Debrief",
    lead:
      "Compress two days of speeches and workshops into a structured 60-second living deliverable that teams are far more likely to finish.",
    problem:
      "After a two-day corporate retreat, employees rarely watch dense four-hour replay videos. Static PDF recap decks sit unread and key decisions disappear into the archive.",
    layout: "Storyboard",
    layoutDetail: "Strict chronological sequence of 4 to 6 slots",
    workflowTitle: "Cadenced sequence",
    workflow: [
      "Slot 1: CEO opening address — strategic vision in 15 seconds.",
      "Slots 2 & 3: workshop debriefs from team directors — concise pitches of the solutions found.",
      "Slot 4: team-building energy or evening-gala highlights.",
      "Slot 5: management call-to-action for the upcoming quarter.",
      "Each speaker records immediately after the intervention. AI balances sound, applies company branding and assembles a dynamic 60-second recap that can be shared the same evening.",
    ],
    impact:
      "The attached concept targets an average completion rate above 85% while giving the event agency a premium, high-value post-event deliverable.",
    monetization:
      "The agency can package the Living Deliverable as a premium B2B add-on and reward certified speakers or facilitators according to verified completion and compliance milestones.",
    image: event4Image,
  },
  {
    id: "pavilion-relay",
    number: "05",
    category: "Trade shows, expos & B2B fairs",
    title: "The Pavilion Relay & Dynamic Sponsoring",
    lead:
      "Replace the static floor plan with a cinematic route that passes attention from host to booth, and can change while the event is still running.",
    problem:
      "In large industrial and technology expos, booths located in secondary aisles receive less foot traffic. Paper guides and 2D maps rarely create meaningful engagement on their own.",
    layout: "Relay",
    layoutDetail: "Cinematic continuity and spatial baton-passing",
    workflowTitle: "A live route through the exhibition hall",
    workflow: [
      "A B2B host or creator opens the Weave at the hall entrance and passes the visual baton to sponsored slots filmed directly at partner booths.",
      "A slot can feature a 15-second robotics demonstration, product reveal or executive innovation pitch.",
      "Exhibitors can hot-swap their own slot during the day to announce an unscheduled keynote, demo or happy hour without breaking the master event link.",
    ],
    impact:
      "Unlock dynamic premium visibility packages that organizers can sell to exhibitors while the show is still in progress.",
    monetization:
      "Illustrative performance model: creators and show hosts can earn commissions per attributed click or QR/badge scan, with Smart Escrow supporting transparent automated payouts.",
    image: event5Image,
    video: event5Video,
  },
];

function MediaPanel({ item }) {
  const videoRef = useRef(null);

  const playPreview = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    const playPromise = video.play();
    if (playPromise?.catch) playPromise.catch(() => {});
  };

  const pausePreview = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    const playPromise = video.play();
    if (playPromise?.catch) playPromise.catch(() => {});
  };

  return (
    <div className={`le-mediaGrid ${item.videoPortrait ? "le-mediaGridPortrait" : ""}`}>
      <figure className="le-imageCard">
        <img src={item.image} alt={`${item.title} Weavevo concept`} loading="lazy" />
      </figure>

      {item.video ? (
        <div
          className={`le-videoCard ${item.videoPortrait ? "is-portrait" : ""}`}
          onMouseEnter={playPreview}
          onMouseLeave={pausePreview}
        >
          <video
            ref={videoRef}
            controls
            autoPlay
            muted
            loop
            preload="auto"
            playsInline
            poster={item.image}
            onMouseEnter={playPreview}
            onMouseLeave={pausePreview}
          >
            <source src={item.video} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      ) : null}
    </div>
  );
}

function ExperienceSection({ item, index }) {
  const sectionClassName = [
    "le-experience",
    index % 2 ? "is-reversed" : "",
    item.sideTitle ? "has-side-title" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const sectionHeading = (
    <>
      <div className="le-kickerRow">
        <span className="le-number">{item.number}</span>
        <span className="le-category">{item.category}</span>
      </div>
      <h2>{item.title}</h2>
    </>
  );

  return (
    <section className={sectionClassName} id={item.id}>
      {!item.sideTitle ? <div className="le-experienceHeader">{sectionHeading}</div> : null}

      <div className="le-experienceCopy">
        {item.sideTitle ? <div className="le-sideTitle">{sectionHeading}</div> : null}
        <p className="le-lead">{item.lead}</p>

        <div className="le-problemCard">
          <span>Problem solved</span>
          <p>{item.problem}</p>
        </div>

        <div className="le-layoutBadge">
          <div>
            <small>Weavevo layout</small>
            <strong>{item.layout}</strong>
          </div>
          <p>{item.layoutDetail}</p>
        </div>

        <div className="le-workflow">
          <h3>{item.workflowTitle}</h3>
          <ol>
            {item.workflow.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>

        <div className="le-outcomes" aria-label="Organizer and Weaver outcomes">
          <article>
            <span>Organizer impact</span>
            <p>{item.impact}</p>
          </article>
          <article>
            <span>Weaver monetization</span>
            <p>{item.monetization}</p>
          </article>
        </div>
      </div>

      <MediaPanel item={item} />
    </section>
  );
}

export default function LiveEventsPage() {
  useEffect(() => {
    document.title = "Live Events | Weavevo";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="le-page">
      <header className="hv2-nav hv2-navPersistent le-header" aria-label="Weavevo navigation">
        <a className="hv2-brand" href="/" aria-label="Weavevo home">
          <img src={logo} alt="Weavevo" />
          <span>weavevo</span>
        </a>
        <nav className="hv2-topMenu hv2-topMenuExpanded" aria-label="Home page sections">
          <a href="/homeV2#product">Product</a>
          <a href="/homeV2#highlights">Features</a>
          <a href="/homeV2#use-cases">Use Cases</a>
          <a href="/homeV2#technology">Technology</a>
          <a href="/homeV2#weavers">Weavers</a>
          <a href="/homeV2#footer-resources">Resources</a>
          <a href="/homeV2#footer-company">Company</a>
          <a className="hv2-headerLoginLink" href="/homeV2#product">Log in</a>
        </nav>
      </header>

      <main>
        <section className="le-hero">
          <img className="le-heroImage" src={event1Image} alt="Live concert crowd, stage and Weavevo Symphony concept" />
          <div className="le-heroScrim" />
          <div className="le-heroContent">
            <span className="le-eyebrow">WEAVEVO USE CASE · LIVE EVENTS</span>
            <h1>Turn live moments into living media.</h1>
            <p>
              Concerts, theatre, festivals, corporate conventions and trade shows become collaborative experiences built from synchronized human perspectives, AI-assisted curation and traceable Weaver participation.
            </p>
          </div>
        </section>

        <div className="le-experienceList">
          {experiences.map((item, index) => (
            <ExperienceSection item={item} index={index} key={item.id} />
          ))}
        </div>

        <section className="le-finalCta">
          <span className="le-sectionEyebrow">BUILD THE NEXT LIVE WEAVE</span>
          <h2>Your event already has hundreds of perspectives. Give them one story.</h2>
          <p>
            Start with a concert, festival, performance, corporate convention or expo and configure the layout, contribution rules, AI guardrails and monetization model around the event.
          </p>
          <div className="le-finalDownloadActions" aria-label="Download Weavevo">
            <a
              className="le-storeButton le-storeButtonPrimary"
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Open Weavevo on the Play Store"
            >
              <i className="bi bi-google-play" aria-hidden="true" />
              <span>Play Store</span>
            </a>
            <button
              className="le-storeButton le-storeButtonSecondary"
              type="button"
              disabled
              aria-label="Weavevo App Store button"
              title="App Store"
            >
              <i className="bi bi-apple" aria-hidden="true" />
              <span>App Store</span>
            </button>
          </div>
        </section>
      </main>

      <footer className="hv2-footerV2 hv2-footerV2Mega le-footer" id="site-footer" aria-label="Weavevo footer">
        <div className="hv2-footerMegaGrid">
          <div className="hv2-footerBrandColumn">
            <a className="hv2-footerBrand" href="/homeV2" aria-label="Weavevo home">
              <img src={logo} alt="" aria-hidden="true" />
              <span>weavevo</span>
            </a>
            <p>Living media built from connected human perspectives, intelligent curation and evolving video experiences.</p>
            <a className="hv2-footerContact" href="/contact">Contact / Support</a>
          </div>

          <div className="hv2-footerColumn">
            <h4>Product</h4>
            <a href="/homeV2#product">Overview</a>
            <a href="/homeV2#highlights">Features</a>
            <a href="/homeV2#technology">Technology</a>
            <a href="/homeV2#weavers">Weaver Network</a>
          </div>

          <div className="hv2-footerColumn" data-usecases>
            <h4>Use Cases</h4>
            <a href="/use-cases/booking">Booking</a>
            <a href="/use-cases/hotel">Hotel</a>
            <a href="/use-cases/real-estate">Real Estate</a>
            <a href="/use-cases/museum">Museum</a>
            <a href="/use-cases/live-events" aria-current="page">Live Events</a>
            <a href="/use-cases/sport">Sport</a>
            <a href="/use-cases/media">Media</a>
          </div>

          <div className="hv2-footerColumn" id="footer-resources">
            <h4>Resources</h4>
            <a href="/living-weave-pipeline">Living Weave Pipeline</a>
            <a href="/earning-model">Earning model</a>
            <a href="/homeV2#highlights">AI & Automation</a>
            <a href="/homeV2#weavers">For Weavers</a>
            <a href="/faq">Help & Support</a>
          </div>

          <div className="hv2-footerColumn" id="footer-company">
            <h4>Company</h4>
            <a href="/about">About Weavevo</a>
            <a href="/mentions-legales">Legal notice</a>
            <a href="/politique-confidentialite">Privacy policy</a>
            <a href="/cgu">Terms of use</a>
            <a href="/accessibilite-rgpd">Accessibility & GDPR</a>
          </div>
        </div>

        <div className="hv2-footerBottomLine hv2-footerMegaBottom">
          <span>© 2026 Weavevo. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
